import React, { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  ArrowRight, 
  ArrowLeft, 
  Lightbulb, 
  Info, 
  Plus, 
  X,
  Sparkles,
  Users,
  HelpCircle,
  ShieldCheck,
  BookOpen
} from "lucide-react";
import StepWrapper from "./StepWrapper";
import { WhyItMatters } from "./HelpComponents";

const TagInput = ({ items, onAdd, onRemove, placeholder, icon: Icon }) => {
  const [inputValue, setInputValue] = useState("");

  const handleAdd = () => {
    if (inputValue.trim() && !items.includes(inputValue.trim())) {
      onAdd(inputValue.trim());
      setInputValue("");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <Input
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="flex-1 bg-background border-input"
        />
        <Button 
          type="button" 
          variant="outline" 
          size="icon"
          onClick={handleAdd}
          disabled={!inputValue.trim()}
        >
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      {items.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {items.map((item, index) => (
            <Badge 
              key={index} 
              variant="secondary"
              className="px-3 py-1.5 text-sm bg-secondary text-secondary-foreground"
            >
              {Icon && <Icon className="w-3 h-3 mr-1.5" />}
              {item}
              <button
                onClick={() => onRemove(index)}
                className="ml-2 hover:text-destructive transition-colors"
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
};

const StepAlternativesInfo = ({ data, updateData, onNext, onBack, coachMode = true }) => {
  const addToArray = (field, value) => {
    updateData(field, [...data[field], value]);
  };

  const removeFromArray = (field, index) => {
    updateData(field, data[field].filter((_, i) => i !== index));
  };

  return (
    <StepWrapper
      stepNumber={3}
      title="Alternatives & Information"
      subtitle={coachMode ? "Explore your options and identify what information you need." : null}
    >
      <div className="space-y-6">
        {/* Introduction Context - only in coach mode */}
        {coachMode && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-primary/5 border border-primary/20 rounded-lg p-4"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <BookOpen className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground text-sm mb-1">Expanding Your Options</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Most people consider too few options. Before settling, push yourself to generate 
                  more choices — including unconventional ones. Then identify what information 
                  would help you compare them fairly.
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Alternatives Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="p-6 bg-card border-border shadow-card">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-chain-alternatives/20 flex items-center justify-center">
                <Lightbulb className="w-5 h-5 text-chain-alternatives" />
              </div>
              <div>
                <h2 className="font-semibold text-foreground">Explore Alternatives</h2>
                <p className="text-sm text-muted-foreground">What options do you have?</p>
              </div>
            </div>

            <WhyItMatters>
              The quality of your decision is limited by the quality of your alternatives. 
              If you only consider two obvious options, you might miss a better third choice. 
              Brainstorm freely before filtering.
            </WhyItMatters>

            <div className="space-y-6 mt-4">
              {/* Options I see */}
              <div>
                <Label className="text-sm font-medium text-foreground mb-1 block">
                  What options do I see?
                </Label>
                <p className="text-xs text-muted-foreground mb-2">
                  List all the choices you&apos;re currently considering — even ones you might rule out later.
                </p>
                <TagInput
                  items={data.optionsISee}
                  onAdd={(value) => addToArray("optionsISee", value)}
                  onRemove={(index) => removeFromArray("optionsISee", index)}
                  placeholder="Add an option..."
                  icon={Lightbulb}
                />
              </div>

              {/* Constraint-free thinking */}
              <div className="p-4 bg-accent/50 rounded-xl">
                <Label htmlFor="constraintFreeOptions" className="text-sm font-medium text-foreground mb-2 block">
                  <Sparkles className="w-4 h-4 inline mr-2 text-accent-warm" />
                  If I didn't have to worry about anything, what else could I do?
                </Label>
                <Textarea
                  id="constraintFreeOptions"
                  value={data.constraintFreeOptions}
                  onChange={(e) => updateData("constraintFreeOptions", e.target.value)}
                  placeholder="Let go of limitations... if money, time, or other concerns weren't a factor, what would you do?"
                  className="min-h-[80px] resize-none bg-background border-input"
                />
              </div>

              {/* Creative approaches */}
              <div>
                <Label className="text-sm font-medium text-foreground mb-2 block">
                  How could I approach this creatively?
                </Label>
                <TagInput
                  items={data.creativeApproaches}
                  onAdd={(value) => addToArray("creativeApproaches", value)}
                  onRemove={(index) => removeFromArray("creativeApproaches", index)}
                  placeholder="Add a creative idea..."
                  icon={Sparkles}
                />
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Information Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="p-6 bg-card border-border shadow-card">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-chain-information/20 flex items-center justify-center">
                <Info className="w-5 h-5 text-chain-information" />
              </div>
              <div>
                <h2 className="font-semibold text-foreground">Gather Information</h2>
                <p className="text-sm text-muted-foreground">What do you need to know?</p>
              </div>
            </div>

            <WhyItMatters>
              Good information reduces uncertainty. Focus on what would actually change your decision — 
              don&apos;t just gather data for data&apos;s sake. Consider the reliability of your sources too.
            </WhyItMatters>

            <div className="space-y-6">
              {/* What I need to know */}
              <div>
                <Label className="text-sm font-medium text-foreground mb-2 block">
                  <Info className="w-4 h-4 inline mr-2" />
                  What do I need to know?
                </Label>
                <TagInput
                  items={data.whatINeedToKnow}
                  onAdd={(value) => addToArray("whatINeedToKnow", value)}
                  onRemove={(index) => removeFromArray("whatINeedToKnow", index)}
                  placeholder="Add information you need..."
                />
              </div>

              {/* Who to talk to */}
              <div>
                <Label className="text-sm font-medium text-foreground mb-2 block">
                  <Users className="w-4 h-4 inline mr-2" />
                  Who should I talk to?
                </Label>
                <TagInput
                  items={data.whoToTalkTo}
                  onAdd={(value) => addToArray("whoToTalkTo", value)}
                  onRemove={(index) => removeFromArray("whoToTalkTo", index)}
                  placeholder="Add people to consult..."
                  icon={Users}
                />
              </div>

              {/* Uncertainties */}
              <div>
                <Label className="text-sm font-medium text-foreground mb-2 block">
                  <HelpCircle className="w-4 h-4 inline mr-2" />
                  What am I unsure about?
                </Label>
                <TagInput
                  items={data.uncertainties}
                  onAdd={(value) => addToArray("uncertainties", value)}
                  onRemove={(index) => removeFromArray("uncertainties", index)}
                  placeholder="Add uncertainties..."
                  icon={HelpCircle}
                />
              </div>

              {/* Source trustworthiness */}
              <div>
                <Label htmlFor="sourceTrustworthiness" className="text-sm font-medium text-foreground mb-2 block">
                  <ShieldCheck className="w-4 h-4 inline mr-2" />
                  How trustworthy are my sources?
                </Label>
                <Textarea
                  id="sourceTrustworthiness"
                  value={data.sourceTrustworthiness}
                  onChange={(e) => updateData("sourceTrustworthiness", e.target.value)}
                  placeholder="Consider the reliability of your information sources..."
                  className="min-h-[80px] resize-none bg-background border-input"
                />
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="flex justify-between items-center pt-4"
        >
          <Button
            variant="ghost"
            onClick={onBack}
            className="text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          <Button
            onClick={onNext}
            className="bg-primary hover:bg-primary/90 text-primary-foreground px-6"
          >
            Continue
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </motion.div>
      </div>
    </StepWrapper>
  );
};

export default StepAlternativesInfo;
